import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Camera, CheckCircle2, CreditCard, DollarSign, Keyboard, QrCode, RefreshCw, Search, Settings, ShoppingCart, Smartphone, User, X } from "lucide-react";
import type { AppCustomer, BookMetadata, PosDiscountDefinition, PosRegisterSession, PosSale, PublicStoreProfile, UserSession } from "../shared/types";
import { isValidIsbn, normalizeIsbn } from "../shared/isbn";
import { StaffApiError, staffErrorMessage } from "../client/api";

type ApiClient = <T>(path: string, init?: RequestInit) => Promise<T>;
type Register = { id: string; name: string; square_location_id?: string; terminal_device_id?: string };
type Location = { id: string; name: string; code: string; active: boolean };
type Candidate = {
  variationId: string;
  copyId?: string;
  trackingMode?: "quantity" | "copy";
  title: string;
  contributor?: string;
  format?: string;
  condition?: string;
  sku?: string;
  storeBarcode?: string;
  unitPriceAmount: number;
  currency?: string;
  availableQuantity: number;
};
type CreditAccount = {
  id: string;
  customerId: string;
  balanceAmount: number;
  heldAmount: number;
  availableBalance: number;
  currency: string;
  status: string;
  creditMode?: "dollar_amount" | "one_for_one";
};
type StoreCreditHold = {
  id: string;
  saleId: string;
  accountId: string;
  customerId: string;
  heldAmount: number;
  availableBalance: number;
  postedBalance: number;
  currency: string;
  status: string;
  version: number;
  capturedAmount?: number;
  ledgerEntryId?: string;
  paymentAttemptId?: string;
};
type LoyaltyBalance = {
  balance: number;
  lifetimeEarned: number;
  lifetimeRedeemed: number;
  expiringPoints?: { points: number; expiresAt: string }[];
  currentLevel?: string;
  nextLevel?: { name: string; pointsNeeded: number };
  availableRewards: { id: string; name: string; pointsCost: number; rewardAmount: number; currency: string; rewardType: string }[];
};
type Tender = "cash" | "square" | "square_app" | "store_credit";
type PendingSquare = { saleId: string; paymentAttemptId?: string; terminalCheckoutId?: string; status?: string; idempotencyKey?: string; kind?: "terminal" | "square_app" };
type GiftCardIssuance = { id: string; saleId: string; saleLineId: string; status: "pending" | "activated" | "failed"; amount: number; currency: string; squareGiftCardGan?: string; failureMessage?: string };
type SquareAppCheckout = { sale: PosSale; paymentAttemptId: string; iosUrl: string; androidUrl: string };
type DetectedBarcode = { rawValue: string };
type BarcodeDetectorLike = {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
};
type BarcodeDetectorConstructor = new (options: { formats: string[] }) => BarcodeDetectorLike;
type ScannerSession = { id: string; targetId: string; url: string; status: string; expiresAt: string };
type ScannerScan = { id: string; input: string; status: string; message?: string; createdAt: string };
type DiscountDraft = { lineId: string; discountDefinitionId: string; discountType: "percentage" | "fixed"; value: string; reason: string };

const pendingSquareStorageKey = "spine_pos_pending_square";
const registerCameraAlwaysOnStorageKey = "spine_pos_camera_always_on";
const squareAppReturnStorageKey = "spine_pos_square_app_returning";
const emptyDiscountDraft: DiscountDraft = { lineId: "", discountDefinitionId: "", discountType: "percentage", value: "", reason: "" };

export function PosRegister({ api, storeProfile, currentUser }: { api: ApiClient; storeProfile: PublicStoreProfile; currentUser: UserSession }) {
  const scanRef = useRef<HTMLInputElement | null>(null);
  const autoSaleInFlightRef = useRef(false);
  const saleRef = useRef<PosSale | null>(null);
  const phoneScannerProcessingRef = useRef(false);
  const [registers, setRegisters] = useState<Register[]>([]);
  const [registerId, setRegisterId] = useState("");
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState("");
  const [features, setFeatures] = useState({ phase9FeaturesEnabled: false, storeCreditRedemptionEnabled: false });
  const [session, setSession] = useState<PosRegisterSession | null>(null);
  const [sale, setSale] = useState<PosSale | null>(null);
  const [scan, setScan] = useState("");
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [cashTendered, setCashTendered] = useState("");
  const [tender, setTender] = useState<Tender>("cash");
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState<AppCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<AppCustomer | null>(null);
  const [creditAccount, setCreditAccount] = useState<CreditAccount | null>(null);
  const [loyaltyBalance, setLoyaltyBalance] = useState<LoyaltyBalance | null>(null);
  const [loyaltyError, setLoyaltyError] = useState<string | null>(null);
  const [selectedRewardId, setSelectedRewardId] = useState("");
  const [creditHold, setCreditHold] = useState<StoreCreditHold | null>(null);
  const [pendingSquare, setPendingSquare] = useState<PendingSquare | null>(() => readPendingSquare());
  const [saleLookupQuery, setSaleLookupQuery] = useState("");
  const [recentSales, setRecentSales] = useState<PosSale[]>([]);
  const [selectedSaleDetail, setSelectedSaleDetail] = useState<PosSale | null>(null);
  const [selectedSaleGiftCards, setSelectedSaleGiftCards] = useState<GiftCardIssuance[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [saleLookupOpen, setSaleLookupOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraAlwaysOn, setCameraAlwaysOn] = useState(() => localStorage.getItem(registerCameraAlwaysOnStorageKey) === "true");
  const [phoneScanner, setPhoneScanner] = useState<ScannerSession | null>(null);
  const [phoneScannerMessage, setPhoneScannerMessage] = useState("");
  const [manualItemOpen, setManualItemOpen] = useState(false);
  const [manualItem, setManualItem] = useState({ scannedCode: "", description: "", price: "", quantity: "1" });
  const [giftCardOpen, setGiftCardOpen] = useState(false);
  const [giftCardAmount, setGiftCardAmount] = useState("");
  const [discountDefinitions, setDiscountDefinitions] = useState<PosDiscountDefinition[]>([]);
  const [discountDraft, setDiscountDraft] = useState<DiscountDraft>(emptyDiscountDraft);

  const activeLocation = locations.find((location) => location.id === locationId);
  const currency = sale?.currency ?? creditAccount?.currency ?? storeProfile.currencyCode ?? "USD";
  const saleCustomerMatchesSelection = !sale || !selectedCustomer || sale.customerId === selectedCustomer.id;
  const squareAppCallbackPending = useMemo(() => isSquarePosCallback(new URLSearchParams(window.location.search)), []);
  const cameraControlsAvailable = useMemo(() => supportsRegisterCameraControls(), []);
  const phoneScannerHostAvailable = useMemo(() => !isPhoneLikeDevice(), []);
  const creditMode = creditAccount?.creditMode ?? "dollar_amount";
  const requiredTradeCredits = sale?.lines.reduce((sum, line) => sum + line.quantity, 0) ?? 0;
  const manualDiscountDefinitions = useMemo(() => discountDefinitions.filter((discount) => discount.active && ["manual", "both"].includes(discount.applicationMode)), [discountDefinitions]);
  const eligibleDiscountDefinitions = useMemo(() => sale ? manualDiscountDefinitions.filter((discount) => discountRuleIsEligibleForSale(discount, sale)) : [], [manualDiscountDefinitions, sale]);
  const selectedDiscountDefinition = discountDraft.discountDefinitionId ? discountDefinitions.find((discount) => discount.id === discountDraft.discountDefinitionId) : undefined;
  const storeCreditReady = Boolean(
    features.storeCreditRedemptionEnabled
    && sale
    && selectedCustomer
    && sale.customerId === selectedCustomer.id
    && creditAccount
    && creditAccount.status === "active"
    && creditAccount.currency === sale.currency
    && (creditMode === "one_for_one" ? creditAccount.availableBalance > 0 && requiredTradeCredits > 0 : creditAccount.availableBalance >= sale.amountDue)
    && sale.amountDue > 0
    && sale.lines.length,
  );
  const storeCreditStatusMessage = useMemo(() => {
    if (!sale) return "Start a sale before using store credit.";
    if (!features.storeCreditRedemptionEnabled) return "";
    if (!selectedCustomer) return "Select a customer with an active store-credit account before using this tender.";
    if (sale.customerId && sale.customerId !== selectedCustomer.id) return "The selected customer does not match the customer attached to this sale.";
    if (!creditAccount) return `${selectedCustomer.displayName} does not have an active store-credit account yet.`;
    if (creditAccount.status !== "active") return "This customer's store-credit account is not active.";
    if (creditAccount.currency !== sale.currency) return `This customer's credit is in ${creditAccount.currency}, but this sale is in ${sale.currency}.`;
    if (!sale.lines.length) return "Add items to the cart before using store credit.";
    if (sale.amountDue <= 0) return "There is no remaining balance to pay with store credit.";
    if (creditMode === "one_for_one") {
      if (requiredTradeCredits <= 0) return "Add eligible items before using trade credit.";
      if (creditAccount.availableBalance < requiredTradeCredits) {
        return `Available credit is ${formatCreditBalance(creditAccount.availableBalance, creditAccount.currency, creditMode)}. ${requiredTradeCredits} trade credit${requiredTradeCredits === 1 ? "" : "s"} required for this cart.`;
      }
      return `${Math.min(creditAccount.availableBalance, requiredTradeCredits)} trade credit${Math.min(creditAccount.availableBalance, requiredTradeCredits) === 1 ? "" : "s"} can be applied to this cart.`;
    }
    if (creditAccount.availableBalance < sale.amountDue) {
      return `Available credit is ${formatCreditBalance(creditAccount.availableBalance, creditAccount.currency, creditMode)}. Store credit is full-sale only.`;
    }
    return `Available credit covers this ${formatCents(sale.amountDue, sale.currency)} sale.`;
  }, [creditAccount, creditMode, features.storeCreditRedemptionEnabled, requiredTradeCredits, sale, selectedCustomer]);

  useEffect(() => {
    saleRef.current = sale;
  }, [sale]);

  const loadContext = useCallback(async () => {
    setError(null);
    const [registerData, settingsData, featureData, discountData] = await Promise.all([
      api<{ registers: Register[] }>("/api/pos/registers"),
      api<{ locations: Location[]; defaults: { defaultLocationId?: string } }>("/api/admin/inventory-settings"),
      api<{ features?: { phase9FeaturesEnabled?: boolean; storeCreditRedemptionEnabled?: boolean } }>("/api/pos/features").catch(() => ({ features: { phase9FeaturesEnabled: false, storeCreditRedemptionEnabled: false } })),
      api<{ discounts: PosDiscountDefinition[] }>("/api/admin/pos/discounts").catch((): { discounts: PosDiscountDefinition[] } => ({ discounts: [] })),
    ]);
    const activeLocations = settingsData.locations.filter((location) => location.active);
    const nextRegisterId = registerId || registerData.registers[0]?.id || "";
    const defaultLocationId = settingsData.defaults.defaultLocationId;
    const nextLocationId = locationId || activeLocations.find((location) => location.id === defaultLocationId)?.id || activeLocations[0]?.id || "";
    setRegisters(registerData.registers);
    setLocations(activeLocations);
    setRegisterId(nextRegisterId);
    setLocationId(nextLocationId);
    setFeatures({
      phase9FeaturesEnabled: Boolean(featureData.features?.phase9FeaturesEnabled),
      storeCreditRedemptionEnabled: Boolean(featureData.features?.storeCreditRedemptionEnabled),
    });
    setDiscountDefinitions(discountData.discounts ?? []);
    if (!nextRegisterId || !nextLocationId) {
      setSession(null);
      return;
    }
    const sessionData = await api<{ session: PosRegisterSession | null }>(`/api/pos/register-session/current?${new URLSearchParams({ registerId: nextRegisterId, locationId: nextLocationId })}`);
    setSession(sessionData.session);
  }, [api, locationId, registerId]);

  useEffect(() => {
    void loadContext().catch((caught) => setError(caught));
  }, [loadContext]);

  useEffect(() => {
    if (!selectedCustomer) {
      setCreditAccount(null);
      setLoyaltyBalance(null);
      setLoyaltyError(null);
      return;
    }
    api<{ account: CreditAccount | null }>(`/api/pos/customers/${encodeURIComponent(selectedCustomer.id)}/credit`)
      .then((data) => setCreditAccount(data.account))
      .catch((caught) => {
        setCreditAccount(null);
        setError(caught);
      });
    api<{ balance: LoyaltyBalance }>(`/api/admin/loyalty/customers/${encodeURIComponent(selectedCustomer.id)}`)
      .then((data) => {
        setLoyaltyBalance(data.balance);
        setLoyaltyError(null);
        setSelectedRewardId(data.balance.availableRewards.find((reward) => data.balance.balance >= reward.pointsCost)?.id ?? "");
      })
      .catch((caught) => {
        setLoyaltyBalance(null);
        setSelectedRewardId("");
        setLoyaltyError(staffErrorMessage(caught));
      });
  }, [api, selectedCustomer]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key.toLowerCase() === "s") {
        event.preventDefault();
        scanRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!pendingSquare?.saleId) return;
    api<{ sale: PosSale }>(`/api/pos/sales/${encodeURIComponent(pendingSquare.saleId)}`)
      .then((data) => {
        setSale(data.sale);
        if (data.sale.status === "completed" || data.sale.paymentStatus === "requires_reconciliation") {
          clearPendingSquare();
          setPendingSquare(null);
        }
      })
      .catch(() => undefined);
  }, [api, pendingSquare?.saleId]);

  useEffect(() => {
    if (!cameraControlsAvailable) {
      setCameraOpen(false);
      if (cameraAlwaysOn) {
        setCameraAlwaysOn(false);
        localStorage.setItem(registerCameraAlwaysOnStorageKey, "false");
      }
      return;
    }
    localStorage.setItem(registerCameraAlwaysOnStorageKey, cameraAlwaysOn ? "true" : "false");
    if (manualItemOpen) {
      setCameraOpen(false);
      return;
    }
    if (cameraAlwaysOn && sale?.status === "draft") setCameraOpen(true);
    if (!cameraAlwaysOn) return;
    if (!sale || sale.status !== "draft") setCameraOpen(false);
  }, [cameraAlwaysOn, cameraControlsAvailable, manualItemOpen, sale?.status, sale?.id]);

  const runMutation = async (label: string, action: () => Promise<void>) => {
    setPending(label);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(null);
    }
  };

  const openSession = () => runMutation("open-session", async () => {
    if (!registerId || !locationId) throw new StaffApiError({ status: 409, code: "missing_context", message: "Choose a register and location first" });
    const data = await api<{ session: PosRegisterSession }>("/api/pos/register-sessions", {
      method: "POST",
      body: JSON.stringify({ registerId, locationId, openingCashAmount: 0, idempotencyKey: crypto.randomUUID() }),
    });
    setSession(data.session);
    setMessage(`Opened ${data.session.sessionNumber}`);
    scanRef.current?.focus();
  });

  const beginSale = useCallback(async (prefixMessage?: string) => {
    if (!session) return;
    const data = await api<{ sale: PosSale }>("/api/pos/sales", {
      method: "POST",
      body: JSON.stringify({ registerSessionId: session.id, customerId: selectedCustomer?.id, idempotencyKey: crypto.randomUUID() }),
    });
    setSale(data.sale);
    setCandidates([]);
    setCreditHold(null);
    setCashTendered("");
    setMessage(prefixMessage ? `${prefixMessage} New sale: ${data.sale.saleNumber}` : `Started ${data.sale.saleNumber}`);
    scanRef.current?.focus();
    return data.sale;
  }, [api, selectedCustomer?.id, session]);

  const startSale = () => runMutation("start-sale", async () => {
    await beginSale();
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!isSquarePosCallback(params)) return;
    const saleId = params.get("sale_id") ?? pendingSquare?.saleId ?? "";
    const idempotencyKey = params.get("idempotency_key") ?? pendingSquare?.idempotencyKey ?? "";
    if (!saleId || !idempotencyKey || !session) return;
    const result = parseSquarePosCallback(params);
    void runMutation("square-app-callback", async () => {
      const data = await api<{ sale: PosSale }>(`/api/pos/sales/${encodeURIComponent(saleId)}/checkout/square-app/complete`, {
        method: "POST",
        body: JSON.stringify({
          status: result.status,
          providerPaymentId: result.providerPaymentId,
          failureCode: result.failureCode,
          failureMessage: result.failureMessage,
          idempotencyKey,
        }),
      });
      clearPendingSquare();
      sessionStorage.removeItem(squareAppReturnStorageKey);
      setPendingSquare(null);
      if (data.sale.status === "completed") {
        await beginSale(`${data.sale.saleNumber} completed in Square app.`);
      } else if (result.status === "cancelled") {
        await beginSale("Square app payment was cancelled.");
      } else {
        setSale(data.sale);
        setMessage(`Square app returned ${result.status}`);
      }
      window.history.replaceState(null, "", window.location.pathname);
    });
  }, [api, beginSale, pendingSquare?.idempotencyKey, pendingSquare?.saleId, session]);

  useEffect(() => {
    if (squareAppCallbackPending || !session || pending || pendingSquare || autoSaleInFlightRef.current) return;
    if (sale && !saleCanStartNext(sale)) return;
    autoSaleInFlightRef.current = true;
    void runMutation("auto-start-sale", async () => {
      await beginSale();
    }).finally(() => {
      autoSaleInFlightRef.current = false;
    });
  }, [beginSale, pending, pendingSquare, sale, session, squareAppCallbackPending]);

  const resolveScanValue = async (rawValue: string): Promise<{ status: "processed" | "needs_review" | "failed"; message: string }> => {
    const value = rawValue.trim();
    const activeSale = saleRef.current;
    if (!activeSale || !value) return { status: "failed", message: "No active draft sale is ready for scans." };
    const data = await api<{ resolution: { candidates: Candidate[]; warnings: string[]; kind: string } }>("/api/pos/sales/resolve", {
      method: "POST",
      body: JSON.stringify({ saleId: activeSale.id, input: value }),
    });
    setScan("");
    setCandidates(data.resolution.candidates);
    if (data.resolution.candidates.length === 0) {
      const description = await manualDescriptionForScan(value);
      setManualItem({ scannedCode: value, description, price: "", quantity: "1" });
      setManualItemOpen(true);
      setCameraOpen(false);
      setMessage(description === value ? "No inventory match. Enter a description and price to add it manually." : "No inventory match. Title filled from ISBN metadata.");
      return { status: "needs_review", message: `${value} needs description and price on the register.` };
    }
    if (data.resolution.candidates.length === 1) {
      await addCandidate(data.resolution.candidates[0]);
      return { status: "processed", message: data.resolution.candidates[0].availableQuantity > 0
        ? `${data.resolution.candidates[0].title} added to the sale.`
        : `${data.resolution.candidates[0].title} added with an out-of-stock warning.` };
    }
    setMessage(data.resolution.warnings[0] ?? `${data.resolution.candidates.length} choice(s) found`);
    return { status: "needs_review", message: data.resolution.warnings[0] ?? `${data.resolution.candidates.length} choices need selection on the register.` };
  };

  const resolveScan = () => runMutation("lookup", async () => {
    await resolveScanValue(scan);
  });

  const handleCameraDetected = (value: string) => {
    setScan(value);
    return resolveScanValue(value);
  };

  const startPhoneScanner = () => runMutation("phone-scanner", async () => {
    const activeSale = saleRef.current?.status === "draft" ? saleRef.current : await beginSale();
    if (!activeSale) return;
    const data = await api<{ session: ScannerSession }>(`/api/pos/sales/${encodeURIComponent(activeSale.id)}/scanner-sessions`, {
      method: "POST",
      body: JSON.stringify({ expiresInMinutes: 240 }),
    });
    setPhoneScanner(data.session);
    setPhoneScannerMessage("Phone scanner is paired. Scans will appear in this sale.");
  });

  const stopPhoneScanner = () => runMutation("phone-scanner-stop", async () => {
    if (!phoneScanner) return;
    await api(`/api/pos/scanner-sessions/${encodeURIComponent(phoneScanner.id)}/close`, { method: "POST" });
    setPhoneScanner(null);
    setPhoneScannerMessage("Phone scanner stopped.");
  });

  const processPhoneScannerQueue = useCallback(async () => {
    if (!phoneScanner || phoneScannerProcessingRef.current || manualItemOpen) return;
    phoneScannerProcessingRef.current = true;
    try {
      const data = await api<{ scans: ScannerScan[] }>(`/api/pos/scanner-sessions/${encodeURIComponent(phoneScanner.id)}/scans`);
      for (const scan of data.scans) {
        try {
          const result = await resolveScanValue(scan.input);
          await api(`/api/pos/scanner-sessions/${encodeURIComponent(phoneScanner.id)}/scans/${encodeURIComponent(scan.id)}/ack`, {
            method: "POST",
            body: JSON.stringify(result),
          });
          setPhoneScannerMessage(result.message);
          if (result.status !== "processed") break;
        } catch (caught) {
          const message = staffErrorMessage(caught);
          await api(`/api/pos/scanner-sessions/${encodeURIComponent(phoneScanner.id)}/scans/${encodeURIComponent(scan.id)}/ack`, {
            method: "POST",
            body: JSON.stringify({ status: "failed", message }),
          }).catch(() => undefined);
          setPhoneScannerMessage(message);
        }
      }
    } catch (caught) {
      setPhoneScannerMessage(staffErrorMessage(caught));
    } finally {
      phoneScannerProcessingRef.current = false;
    }
  }, [api, manualItemOpen, phoneScanner, resolveScanValue]);

  useEffect(() => {
    if (!phoneScanner) return undefined;
    void processPhoneScannerQueue();
    const timer = window.setInterval(() => { void processPhoneScannerQueue(); }, 2000);
    return () => window.clearInterval(timer);
  }, [phoneScanner, processPhoneScannerQueue]);

  const manualDescriptionForScan = async (value: string): Promise<string> => {
    const isbn = normalizeIsbn(value);
    if (!isValidIsbn(isbn)) return value;
    try {
      const data = await api<{ results: BookMetadata[] }>("/api/admin/metadata/search", {
        method: "POST",
        body: JSON.stringify({ isbn }),
      });
      return data.results[0]?.title?.trim() || value;
    } catch {
      return value;
    }
  };

  const searchCatalog = () => runMutation("lookup", async () => {
    if (!scan.trim() || !locationId) return;
    const data = await api<{ candidates: Candidate[] }>(`/api/pos/sales/search?${new URLSearchParams({ q: scan, locationId, limit: "20" })}`);
    setCandidates(data.candidates);
    setMessage(data.candidates.length ? `${data.candidates.length} local item(s) found` : "No local item matched that search");
  });

  const addCandidate = async (candidate: Candidate) => {
    const activeSale = saleRef.current;
    if (!activeSale || creditHold?.status === "active") return;
    const data = await api<{ sale: PosSale }>(`/api/pos/sales/${activeSale.id}/lines`, {
      method: "POST",
      body: JSON.stringify({ variationId: candidate.variationId, copyId: candidate.copyId, quantity: 1, idempotencyKey: crypto.randomUUID() }),
    });
    saleRef.current = data.sale;
    setSale(data.sale);
    setCandidates([]);
    setMessage(candidate.availableQuantity > 0
      ? `${candidate.title} reserved for this sale`
      : `${candidate.title} added without an inventory reservation. Verify the count after checkout.`);
    scanRef.current?.focus();
  };

  const addCandidateFromClick = (candidate: Candidate) => runMutation("add-line", async () => addCandidate(candidate));

  const addManualItem = () => runMutation("manual-line", async () => {
    if (!sale) return;
    const description = manualItem.description.trim();
    const quantity = Math.max(1, Math.min(99, Math.floor(Number(manualItem.quantity) || 1)));
    const unitPriceAmount = centsFromInput(manualItem.price);
    if (!description) throw new StaffApiError({ status: 400, code: "manual_description_required", message: "Enter an item description" });
    if (unitPriceAmount <= 0) throw new StaffApiError({ status: 400, code: "manual_price_required", message: "Enter a price greater than zero" });
    const data = await api<{ sale: PosSale }>(`/api/pos/sales/${sale.id}/manual-lines`, {
      method: "POST",
      body: JSON.stringify({ description, quantity, unitPriceAmount, idempotencyKey: crypto.randomUUID() }),
    });
    setSale(data.sale);
    setManualItemOpen(false);
    setManualItem({ scannedCode: "", description: "", price: "", quantity: "1" });
    setMessage(`${description} added manually`);
    scanRef.current?.focus();
  });

  const addGiftCardPurchase = () => runMutation("gift-card-line", async () => {
    if (!sale) return;
    const unitPriceAmount = centsFromInput(giftCardAmount);
    if (unitPriceAmount <= 0) throw new StaffApiError({ status: 400, code: "gift_card_amount_required", message: "Enter a gift card amount greater than zero" });
    const data = await api<{ sale: PosSale }>(`/api/pos/sales/${sale.id}/manual-lines`, {
      method: "POST",
      body: JSON.stringify({ description: "Gift Card", quantity: 1, unitPriceAmount, giftCardPurchase: true, idempotencyKey: crypto.randomUUID() }),
    });
    setSale(data.sale);
    setGiftCardOpen(false);
    setGiftCardAmount("");
    setMessage(`Gift card added for ${formatCents(unitPriceAmount, sale.currency)}`);
    scanRef.current?.focus();
  });

  const removeLine = (lineId: string) => runMutation("remove-line", async () => {
    if (!sale || creditHold?.status === "active") return;
    const data = await api<{ sale: PosSale }>(`/api/pos/sales/${sale.id}/lines/${lineId}`, { method: "DELETE", headers: { "idempotency-key": crypto.randomUUID() } });
    setSale(data.sale);
  });

  const searchCustomers = () => runMutation("customer-search", async () => {
    if (!customerQuery.trim()) return;
    const data = await api<{ customers: AppCustomer[] }>(`/api/pos/customers/search?${new URLSearchParams({ q: customerQuery })}`);
    setCustomerResults(data.customers);
    setMessage(data.customers.length ? `${data.customers.length} customer(s) found` : "No customer matched that search");
  });

  const attachSelectedCustomer = async (customer: AppCustomer) => {
    setSelectedCustomer(customer);
    setCustomerResults([]);
    setCustomerDialogOpen(false);
    if (sale?.status === "draft" && !sale.customerId && sale.lines.length === 0) {
      const data = await api<{ sale: PosSale }>(`/api/pos/sales/${encodeURIComponent(sale.id)}/customer`, {
        method: "PATCH",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      setSale(data.sale);
    }
    setMessage(`Selected customer ${customer.displayName}`);
  };

  const selectCustomer = (customer: AppCustomer) => runMutation("customer-select", async () => {
    await attachSelectedCustomer(customer);
  });

  const createCustomer = () => runMutation("customer-create", async () => {
    const trimmed = customerQuery.trim();
    if (!trimmed) return;
    const [givenName, ...rest] = trimmed.split(/\s+/);
    const data = await api<{ customer: AppCustomer }>("/api/pos/customers", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({ givenName, familyName: rest.join(" ") || undefined }),
    });
    await attachSelectedCustomer(data.customer);
  });

  const enrollCustomerInLoyalty = () => runMutation("loyalty-enroll", async () => {
    if (!selectedCustomer) return;
    const data = await api<{ balance: LoyaltyBalance }>("/api/admin/loyalty/enrollments", {
      method: "POST",
      body: JSON.stringify({ customerId: selectedCustomer.id, termsAccepted: false, source: "checkout", idempotencyKey: crypto.randomUUID() }),
    });
    setLoyaltyBalance(data.balance);
    setLoyaltyError(null);
    setSelectedRewardId(data.balance.availableRewards.find((reward) => data.balance.balance >= reward.pointsCost)?.id ?? "");
    setMessage(`${selectedCustomer.displayName} is enrolled in loyalty`);
  });

  const checkoutCash = () => runMutation("cash", async () => {
    if (!sale) return;
    const fingerprint = JSON.stringify({ saleId: sale.id, total: sale.grandTotalAmount, cashTendered });
    const data = await api<{ sale: PosSale; giftCardIssuances?: GiftCardIssuance[] }>(`/api/pos/sales/${sale.id}/checkout/cash`, {
      method: "POST",
      body: JSON.stringify({ cashTenderedAmount: centsFromInput(cashTendered), idempotencyKey: retainedKey("cash", sale.id, fingerprint) }),
    });
    clearSaleKeys(data.sale.id);
    await beginSale(`Cash sale completed. Change due ${formatCents(data.sale.changeDue, data.sale.currency)}.${giftCardMessage(data.giftCardIssuances)}`);
  });

  const checkoutSquare = () => runMutation("square", async () => {
    if (!sale) return;
    const fingerprint = JSON.stringify({ saleId: sale.id, total: sale.grandTotalAmount, registerId, locationId });
    const data = await api<{ sale: PosSale; paymentAttemptId: string; terminalCheckoutId?: string; terminalStatus?: string }>(`/api/pos/sales/${sale.id}/checkout/square`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: retainedKey("square", sale.id, fingerprint) }),
    });
    const nextPending = { saleId: data.sale.id, paymentAttemptId: data.paymentAttemptId, terminalCheckoutId: data.terminalCheckoutId, status: data.terminalStatus ?? data.sale.paymentStatus, kind: "terminal" as const };
    setSale(data.sale);
    setPendingSquare(nextPending);
    writePendingSquare(nextPending);
    setMessage(`Square Terminal ${nextPending.status ?? "pending"}. Keep this sale open until the server reports completion or reconciliation.`);
  });

  const checkoutSquareApp = () => runMutation("square-app", async () => {
    if (!sale) return;
    const idempotencyKey = crypto.randomUUID();
    const data = await api<SquareAppCheckout>(`/api/pos/sales/${sale.id}/checkout/square-app`, {
      method: "POST",
      body: JSON.stringify({ note: sale.saleNumber, idempotencyKey }),
    });
    const nextPending = { saleId: data.sale.id, paymentAttemptId: data.paymentAttemptId, status: data.sale.paymentStatus, idempotencyKey, kind: "square_app" as const };
    setSale(data.sale);
    setPendingSquare(nextPending);
    writePendingSquare(nextPending);
    setMessage("Opening Square app. Return here after the payment completes.");
    const target = /Android/i.test(navigator.userAgent) ? data.androidUrl : data.iosUrl;
    sessionStorage.setItem(squareAppReturnStorageKey, "1");
    window.location.href = target;
  });

  const refreshSaleStatus = () => runMutation("refresh-sale", async () => {
    if (!sale) return;
    const data = await api<{ sale: PosSale; giftCardIssuances?: GiftCardIssuance[] }>(`/api/pos/sales/${sale.id}`);
    setSale(data.sale);
    if (data.sale.status === "completed") {
      clearPendingSquare();
      setPendingSquare(null);
      const gifts = await api<{ giftCardIssuances: GiftCardIssuance[] }>(`/api/pos/sales/${encodeURIComponent(data.sale.id)}/gift-card-issuances`);
      await beginSale(`${data.sale.saleNumber} completed.${giftCardMessage(gifts.giftCardIssuances)}`);
    } else if (data.sale.paymentStatus === "requires_reconciliation") {
      setMessage(`${data.sale.saleNumber} requires reconciliation`);
      clearPendingSquare();
      setPendingSquare(null);
    } else {
      setMessage(`${data.sale.saleNumber} is ${data.sale.paymentStatus}`);
    }
  });

  const cancelPendingSquareApp = () => runMutation("square-app-cancel", async () => {
    if (!pendingSquare?.saleId || pendingSquare.kind !== "square_app") return;
    const data = await api<{ sale: PosSale }>(`/api/pos/sales/${encodeURIComponent(pendingSquare.saleId)}/checkout/square-app/complete`, {
      method: "POST",
      body: JSON.stringify({
        status: "cancelled",
        failureCode: "customer_cancelled",
        failureMessage: "Square app payment was cancelled before payment",
        idempotencyKey: pendingSquare.idempotencyKey ?? crypto.randomUUID(),
      }),
    });
    clearPendingSquare();
    sessionStorage.removeItem(squareAppReturnStorageKey);
    setPendingSquare(null);
    setSale(data.sale);
    await beginSale("Square app payment was cancelled.");
  });

  useEffect(() => {
    if (!session || squareAppCallbackPending || pending || pendingSquare?.kind !== "square_app") return;
    if (sessionStorage.getItem(squareAppReturnStorageKey) !== "1") return;
    void cancelPendingSquareApp();
  }, [pending, pendingSquare?.kind, session, squareAppCallbackPending]);

  const selectDiscountDefinition = (definition: PosDiscountDefinition) => {
    const matchingLine = sale?.lines.find((line) => discountRuleMatchesLine(definition, line));
    setDiscountDraft({
      lineId: definition.scope === "line" ? matchingLine?.id ?? "" : "",
      discountDefinitionId: definition.id,
      discountType: definition.discountType,
      value: definition.discountType === "percentage" ? (definition.valueAmount / 100).toString() : centsToInput(definition.valueAmount),
      reason: definition.requiresReason ? "" : definition.name,
    });
  };

  const applyDiscount = () => runMutation("apply-discount", async () => {
    if (!sale || creditHold?.status === "active") return;
    const definition = discountDraft.discountDefinitionId ? discountDefinitions.find((discount) => discount.id === discountDraft.discountDefinitionId) : undefined;
    const numeric = Number(discountDraft.value.trim().replace(/[$,%]/g, ""));
    if (!Number.isFinite(numeric) || numeric <= 0) throw new StaffApiError({ status: 400, code: "discount_value_required", message: "Enter a discount amount" });
    if (!discountDraft.reason.trim()) throw new StaffApiError({ status: 400, code: "discount_reason_required", message: "Enter a discount reason" });
    const valueAmount = Math.round(numeric * 100);
    const data = await api<{ sale: PosSale }>(`/api/pos/sales/${sale.id}/discounts`, {
      method: "POST",
      body: JSON.stringify({
        lineId: discountDraft.lineId || undefined,
        discountDefinitionId: discountDraft.discountDefinitionId || undefined,
        discountType: discountDraft.discountType,
        valueAmount: definition?.valueAmount ?? valueAmount,
        reason: discountDraft.reason,
        idempotencyKey: crypto.randomUUID(),
      }),
    });
    setSale(data.sale);
    setDiscountDraft(emptyDiscountDraft);
    setMessage("Discount applied");
  });

  const checkoutStoreCredit = () => runMutation("store-credit", async () => {
    if (!sale || !storeCreditReady) return;
    const holdFingerprint = JSON.stringify({ saleId: sale.id, total: sale.amountDue, customerId: sale.customerId, currency: sale.currency });
    const holdData = creditHold?.status === "active"
      ? { hold: creditHold }
      : await api<{ hold: StoreCreditHold }>(`/api/pos/sales/${sale.id}/store-credit-holds`, {
        method: "POST",
        body: JSON.stringify({ idempotencyKey: retainedKey("credit-hold", sale.id, holdFingerprint) }),
      });
    setCreditHold(holdData.hold);
    const captureFingerprint = JSON.stringify({ saleId: sale.id, holdId: holdData.hold.id, version: holdData.hold.version, total: sale.amountDue });
    const capture = await api<{ hold: StoreCreditHold; sale: PosSale }>(`/api/pos/sales/${sale.id}/store-credit-holds/${holdData.hold.id}/capture`, {
      method: "POST",
      body: JSON.stringify({ expectedStatus: "active", expectedVersion: holdData.hold.version, idempotencyKey: retainedKey("credit-capture", sale.id, captureFingerprint) }),
    });
    setCreditHold(capture.hold);
    if (selectedCustomer) void api<{ account: CreditAccount | null }>(`/api/pos/customers/${encodeURIComponent(selectedCustomer.id)}/credit`).then((data) => setCreditAccount(data.account));
    clearSaleKeys(capture.sale.id);
    await beginSale(`Store credit captured for ${capture.sale.saleNumber}.`);
  });

  const applyLoyaltyReward = () => runMutation("loyalty-reward", async () => {
    if (!sale || !selectedCustomer || !selectedRewardId) return;
    const fingerprint = JSON.stringify({ saleId: sale.id, customerId: selectedCustomer.id, rewardDefinitionId: selectedRewardId, total: sale.amountDue });
    const data = await api<{ sale: PosSale; balance: LoyaltyBalance; rewardAmount: number }>(`/api/pos/sales/${sale.id}/loyalty-redemptions`, {
      method: "POST",
      body: JSON.stringify({ customerId: selectedCustomer.id, rewardDefinitionId: selectedRewardId, idempotencyKey: retainedKey("loyalty", sale.id, fingerprint) }),
    });
    setSale(data.sale);
    setLoyaltyBalance(data.balance);
    setLoyaltyError(null);
    setMessage(`Applied loyalty reward for ${formatCents(data.rewardAmount, data.sale.currency)}`);
  });

  const releaseActiveCreditHold = async () => {
    if (!sale || !creditHold || creditHold.status !== "active") return;
    const release = await api<{ hold: StoreCreditHold }>(`/api/pos/sales/${sale.id}/store-credit-holds/${creditHold.id}/release`, {
      method: "POST",
      body: JSON.stringify({ expectedStatus: "active", expectedVersion: creditHold.version, idempotencyKey: crypto.randomUUID() }),
    });
    setCreditHold(release.hold);
    setMessage("Store-credit hold released");
  };

  const releaseCreditHold = () => runMutation("release-credit", releaseActiveCreditHold);

  const cancelSale = () => runMutation("cancel-sale", async () => {
    if (!sale || pendingSquare) return;
    if (creditHold?.status === "active") await releaseActiveCreditHold();
    const data = await api<{ sale: PosSale }>(`/api/pos/sales/${sale.id}/cancel`, { method: "POST", body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }) });
    setSale(data.sale);
    setMessage(`${data.sale.saleNumber} cancelled`);
    clearSaleKeys(data.sale.id);
  });

  const loadRecentSales = () => runMutation("sale-lookup", async () => {
    const data = await api<{ sales: PosSale[] }>("/api/pos/sales");
    const query = saleLookupQuery.trim().toLowerCase();
    setRecentSales(query ? data.sales.filter((candidate) => [candidate.id, candidate.saleNumber, candidate.customerId, candidate.status, candidate.paymentStatus].filter(Boolean).some((part) => String(part).toLowerCase().includes(query))) : data.sales.slice(0, 12));
  });

  const readSaleDetail = (saleId: string) => runMutation("sale-detail", async () => {
    const data = await api<{ sale: PosSale }>(`/api/pos/sales/${encodeURIComponent(saleId)}`);
    const giftCards = await api<{ giftCardIssuances: GiftCardIssuance[] }>(`/api/pos/sales/${encodeURIComponent(saleId)}/gift-card-issuances`);
    setSelectedSaleDetail(data.sale);
    setSelectedSaleGiftCards(giftCards.giftCardIssuances);
  });

  const retryGiftCardActivation = (saleId: string) => runMutation("gift-card-retry", async () => {
    const data = await api<{ giftCardIssuances: GiftCardIssuance[] }>(`/api/pos/sales/${encodeURIComponent(saleId)}/gift-card-issuances/activate`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
    setSelectedSaleGiftCards(data.giftCardIssuances);
    setMessage(`Gift card activation checked.${giftCardMessage(data.giftCardIssuances)}`);
  });

  const readyToMutate = !pending && session && sale && sale.lines.length > 0 && !pendingSquare;
  const cashTenderedAmount = centsFromInput(cashTendered);
  const cashReady = Boolean(readyToMutate && cashTenderedAmount >= (sale?.grandTotalAmount ?? 0));

  const orderCount = sale?.lines.reduce((sum, line) => sum + line.quantity, 0) ?? 0;

  return (
    <section className="page pos-page">
      <header className="pos-register-bar" aria-label="Register controls">
        <div className="pos-register-title">
          <p className="eyebrow">PaperbackOS POS</p>
          <h1>Register</h1>
        </div>
        <div className="pos-register-actions">
          {!session && <button className="primary" disabled={Boolean(pending) || !registerId || !locationId} onClick={openSession}><DollarSign /> Open register</button>}
          {session && pending === "auto-start-sale" && <button className="primary" disabled><ShoppingCart /> Starting sale</button>}
          <button onClick={() => void loadContext()} aria-label="Refresh register context"><RefreshCw /> Refresh</button>
          <div className="setup-menu">
            <button
              className="icon"
              aria-expanded={setupOpen}
              aria-haspopup="menu"
              aria-label="Register setup"
              onClick={() => setSetupOpen((open) => !open)}
            >
              <Settings />
            </button>
            {setupOpen && (
              <div className="setup-menu-panel" aria-label="Register setup options">
                {registers.length === 0 && <div className="feature-panel"><AlertTriangle /> No active register is configured for this environment. Add a register through the approved seed or admin configuration before selling.</div>}
                <label>Register
                  <select value={registerId} disabled={Boolean(sale && !saleCanStartNext(sale))} onChange={(event) => { setRegisterId(event.target.value); setSession(null); }}>
                    {registers.map((register) => <option key={register.id} value={register.id}>{register.name}</option>)}
                  </select>
                </label>
                <label>Location
                  <select value={locationId} disabled={Boolean(sale && !saleCanStartNext(sale))} onChange={(event) => { setLocationId(event.target.value); setSession(null); }}>
                    {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                  </select>
                </label>
                <button onClick={() => { setSaleLookupOpen(true); setSetupOpen(false); }}><Search /> Find completed sale</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <ApiErrorCallout error={error} />
      {message && <div className="success compact-status" role="status" aria-live="polite"><CheckCircle2 /> {message}</div>}

      <div className="pos-layout">
        <div className="pos-workspace">
          <section className="register-customer-bar" aria-label="Customer for sale">
            {selectedCustomer ? (
              <div className="customer-chip">
                <User />
                <strong>{selectedCustomer.displayName}</strong>
                <span>{creditAccount ? `${formatCreditBalance(creditAccount.availableBalance, creditAccount.currency, creditMode)} available` : "No credit account"}</span>
                <button aria-label="Remove customer from sale" disabled={Boolean(sale && sale.customerId === selectedCustomer.id)} onClick={() => { setSelectedCustomer(null); setCreditAccount(null); }}>
                  <X />
                </button>
              </div>
            ) : (
              <>
                <span>No customer selected</span>
                <button onClick={() => setCustomerDialogOpen(true)}><User /> Select customer</button>
              </>
            )}
            {selectedCustomer && <button onClick={() => setCustomerDialogOpen(true)} disabled={Boolean(sale && sale.customerId === selectedCustomer.id)}>Change customer</button>}
          </section>
          {!saleCustomerMatchesSelection && <div className="error"><AlertTriangle /> Current sale was started with a different customer. Start a new sale to use this customer for store credit.</div>}
          {selectedCustomer && (
            <section className="register-panel loyalty-register-panel" aria-label="Customer loyalty">
              <div className="loyalty-register-summary">
                <strong>{loyaltyBalance ? `${loyaltyBalance.balance} loyalty points` : "Loyalty unavailable"}</strong>
                {loyaltyBalance ? (
                  <>
                    <span>{loyaltyBalance.currentLevel ?? "Reader"}{loyaltyBalance.nextLevel ? ` / ${loyaltyBalance.nextLevel.pointsNeeded} pts to ${loyaltyBalance.nextLevel.name}` : " / highest active level"}</span>
                    <span>{loyaltyBalance.availableRewards.filter((reward) => loyaltyBalance.balance >= reward.pointsCost).length} reward(s) currently available</span>
                    {loyaltyBalance.expiringPoints?.[0] && <span>{loyaltyBalance.expiringPoints[0].points} pts expire {formatDate(loyaltyBalance.expiringPoints[0].expiresAt)}</span>}
                    {sale && <span>{loyaltyQualificationText(sale, loyaltyBalance, selectedRewardId)}</span>}
                  </>
                ) : (
                  <span>{loyaltyError ? `Checkout can continue: ${loyaltyError}` : "Select enroll or refresh before applying rewards."}</span>
                )}
              </div>
              {loyaltyBalance?.availableRewards.length ? (
                <div className="loyalty-reward-row">
                  <label>Reward
                    <select value={selectedRewardId} onChange={(event) => setSelectedRewardId(event.target.value)}>
                      <option value="">Choose reward</option>
                      {loyaltyBalance.availableRewards.map((reward) => <option key={reward.id} value={reward.id} disabled={loyaltyBalance.balance < reward.pointsCost}>{reward.name} ({reward.pointsCost} pts)</option>)}
                    </select>
                  </label>
                  <button disabled={!sale || sale.status !== "draft" || !selectedRewardId || Boolean(pending)} onClick={applyLoyaltyReward}>Apply reward</button>
                </div>
              ) : <p className="empty-inline">No active loyalty rewards are configured.</p>}
              <div className="loyalty-reward-row">
                <button disabled={Boolean(pending)} onClick={enrollCustomerInLoyalty}><User /> Enroll or confirm</button>
                {loyaltyError && <button disabled={Boolean(pending)} onClick={enrollCustomerInLoyalty}><RefreshCw /> Retry loyalty</button>}
              </div>
            </section>
          )}

          <section className="register-panel scan-panel pos-product-search" id="register-scan">
            <div className="scan-row">
              <label className="scan-input">Scan store barcode, ISBN, or SKU
                <input
                  ref={scanRef}
                  value={scan}
                  disabled={!sale || sale.status !== "draft" || Boolean(creditHold?.status === "active")}
                  onChange={(event) => setScan(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void resolveScan();
                    }
                  }}
                  placeholder="Scan then press Enter"
                  aria-describedby="scanner-help"
                />
              </label>
              <button disabled={!sale || !scan.trim() || Boolean(pending)} onClick={searchCatalog}><Search /> Search inventory</button>
            </div>
            <div className="pos-filter-strip" aria-label="Register search helpers">
              <span className="active">All local inventory</span>
              <span>Barcode</span>
              <span>ISBN</span>
              <span>SKU</span>
              <span>Title or author</span>
            </div>
            <p id="scanner-help" className="empty-inline"><Keyboard /> Alt+S focuses scan. Scan Enter only searches; checkout requires a button press.</p>
            {phoneScannerHostAvailable && (
              <div className="camera-toggle-row">
                {phoneScanner
                  ? <button aria-pressed disabled={Boolean(pending)} onClick={stopPhoneScanner}><Smartphone /> Stop phone scanner</button>
                  : <button disabled={Boolean(pending) || !session || Boolean(creditHold?.status === "active")} onClick={startPhoneScanner}><QrCode /> Pair phone scanner</button>}
              </div>
            )}
            <div className="camera-toggle-row">
              <button disabled={!sale || sale.status !== "draft" || Boolean(pending) || Boolean(creditHold?.status === "active")} onClick={() => setGiftCardOpen(true)}><CreditCard /> Gift card</button>
            </div>
            {phoneScanner && <PhoneScannerPairingCard session={phoneScanner} message={phoneScannerMessage} />}
            {cameraControlsAvailable && (
              <>
                <div className="camera-toggle-row">
                  <button aria-pressed={cameraOpen} disabled={!sale || sale.status !== "draft"} onClick={() => setCameraOpen((open) => !open)}><Camera /> {cameraOpen ? "Stop camera" : "Camera scan"}</button>
                  <label className="checkbox-row"><input type="checkbox" checked={cameraAlwaysOn} onChange={(event) => setCameraAlwaysOn(event.target.checked)} /> Always on</label>
                </div>
                {cameraOpen && sale?.status === "draft" && <RegisterCameraScanner onDetected={handleCameraDetected} onClose={() => { setCameraAlwaysOn(false); setCameraOpen(false); }} />}
              </>
            )}
            {candidates.length === 0 ? <p className="empty compact-empty">Scan or search to fill this area with sellable local inventory.</p> : (
              <div className="register-results">
                {candidates.map((candidate) => (
                  <article className="register-result" key={`${candidate.variationId}-${candidate.copyId ?? "qty"}`}>
                    <div className="pos-result-cover" aria-hidden>{candidate.title.slice(0, 2).toUpperCase()}</div>
                    <div className="register-result-main">
                      <h3>{candidate.title}</h3>
                      <p>{[candidate.contributor, candidate.format, candidate.condition].filter(Boolean).join(" / ")}</p>
                      {candidate.copyId && <p className="empty-inline">Exact copy {candidate.storeBarcode ?? candidate.copyId}</p>}
                      {candidate.availableQuantity <= 0 && <p className="error-text">No available inventory at this location.</p>}
                    </div>
                    <strong>{formatCents(candidate.unitPriceAmount, candidate.currency ?? currency)}</strong>
                    <span>{candidate.availableQuantity} available</span>
                    <button disabled={Boolean(pending) || Boolean(creditHold?.status === "active")} onClick={() => void addCandidateFromClick(candidate)}>Add to cart</button>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="pos-cart" id="register-checkout">
          <div className="pos-cart-header">
            <h2>Order</h2>
            <span>{orderCount}</span>
          </div>
          {!session && <p className="empty-inline">Open a register session before selling.</p>}
          {session && !sale && <p className="empty-inline">Start a sale, then scan books into the cart.</p>}
          {sale && <SaleStatus sale={sale} />}
          <div className="pos-cart-lines" aria-label="Order line items">
          {sale?.lines.map((line) => (
            <div className="pos-cart-line" key={line.id}>
              <div className="cover placeholder">{line.copyId ? "Copy" : "Qty"}</div>
              <div>
                <strong>{line.title}</strong>
                <span>{line.quantity} x {formatCents(line.unitPriceAmount, sale.currency)} {line.copyId ? `/ ${line.storeBarcode ?? line.copyId}` : ""}</span>
                <small>{line.format} {line.condition ? `/ ${line.condition}` : ""}</small>
                {line.variationId && !line.reservationId && <small className="error-text">Out of stock: sale will require an inventory count review.</small>}
                <div className="pos-cart-controls"><button disabled={Boolean(pending) || Boolean(creditHold?.status === "active") || sale.status !== "draft"} onClick={() => void removeLine(line.id)}><X /> Remove</button></div>
              </div>
            </div>
          ))}
          </div>
          {sale && <dl className="metadata-fields"><dt>Subtotal</dt><dd>{formatCents(sale.subtotalAmount, sale.currency)}</dd><dt>Discounts</dt><dd>{formatCents(sale.discountTotalAmount, sale.currency)}</dd><dt>Tax</dt><dd>{formatCents(sale.taxTotalAmount, sale.currency)}</dd><dt>Total</dt><dd>{formatCents(sale.grandTotalAmount, sale.currency)}</dd></dl>}

          {sale && sale.status === "draft" && sale.lines.length > 0 && (
            <section className="register-checkout-panel discount-panel">
              <h3>Discount</h3>
              {eligibleDiscountDefinitions.length > 0 && (
                <div className="discount-rule-list" aria-label="Configured discounts">
                  {eligibleDiscountDefinitions.slice(0, 6).map((discount) => (
                    <button
                      key={discount.id}
                      className={discountDraft.discountDefinitionId === discount.id ? "active" : ""}
                      disabled={Boolean(pending) || Boolean(creditHold?.status === "active") || (currentUser.role === "staff" && discount.requiresManager)}
                      onClick={() => selectDiscountDefinition(discount)}
                    >
                      {discount.name}
                      <span>{formatDiscountValue(discount, sale.currency)}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="form-grid">
                <label className="wide">Rule
                  <select value={discountDraft.discountDefinitionId} onChange={(event) => {
                    const definition = discountDefinitions.find((discount) => discount.id === event.target.value);
                    if (definition) selectDiscountDefinition(definition);
                    else setDiscountDraft(emptyDiscountDraft);
                  }}>
                    <option value="">Custom manager discount</option>
                    {eligibleDiscountDefinitions.map((discount) => <option key={discount.id} value={discount.id} disabled={currentUser.role === "staff" && discount.requiresManager}>{discount.name}</option>)}
                  </select>
                </label>
                <label className="wide">Apply to
                  <select value={discountDraft.lineId} disabled={selectedDiscountDefinition?.scope === "order"} onChange={(event) => setDiscountDraft({ ...discountDraft, lineId: event.target.value })}>
                    <option value="">Whole sale</option>
                    {sale.lines.map((line) => <option key={line.id} value={line.id} disabled={Boolean(selectedDiscountDefinition && !discountRuleMatchesLine(selectedDiscountDefinition, line))}>{line.title}</option>)}
                  </select>
                </label>
                <label>Type
                  <select value={discountDraft.discountType} disabled={Boolean(selectedDiscountDefinition)} onChange={(event) => setDiscountDraft({ ...discountDraft, discountType: event.target.value as "percentage" | "fixed" })}>
                    <option value="percentage">Percent</option>
                    <option value="fixed">Dollar amount</option>
                  </select>
                </label>
                <label>{discountDraft.discountType === "percentage" ? "Percent off" : "Amount off"}<input inputMode="decimal" value={discountDraft.value} disabled={Boolean(selectedDiscountDefinition)} onChange={(event) => setDiscountDraft({ ...discountDraft, value: event.target.value })} placeholder={discountDraft.discountType === "percentage" ? "10" : "5.00"} /></label>
                <label className="wide">Reason<input value={discountDraft.reason} onChange={(event) => setDiscountDraft({ ...discountDraft, reason: event.target.value })} placeholder="Manager promo, damaged cover, customer service" /></label>
                <button className="wide" disabled={Boolean(pending) || Boolean(creditHold?.status === "active")} onClick={applyDiscount}>Apply discount</button>
              </div>
            </section>
          )}

          {sale && !terminalSaleStatus(sale) && (
            <div className="register-checkout-panel">
              <h3>Tender</h3>
              <div className="tender-selector" role="group" aria-label="Tender type">
                <button type="button" className={tender === "cash" ? "active" : ""} aria-pressed={tender === "cash"} onClick={() => setTender("cash")}><DollarSign /> <span>Cash</span></button>
                <button type="button" className={tender === "square" ? "active" : ""} aria-pressed={tender === "square"} onClick={() => setTender("square")}><CreditCard /> <span>Square</span></button>
                <button type="button" className={tender === "square_app" ? "active" : ""} aria-pressed={tender === "square_app"} onClick={() => setTender("square_app")}><CreditCard /> <span>Square app</span></button>
                {features.storeCreditRedemptionEnabled && <button type="button" className={tender === "store_credit" ? "active" : ""} aria-pressed={tender === "store_credit"} onClick={() => setTender("store_credit")}><DollarSign /> <span>Store credit</span></button>}
              </div>
              {!features.storeCreditRedemptionEnabled && <FeatureDisabledPanel />}
              {tender === "cash" && (
                <div className="form-grid">
                  <label className="wide">Cash tendered<input value={cashTendered} onChange={(event) => setCashTendered(event.target.value)} placeholder="0.00" /></label>
                  <button className="primary wide" disabled={!cashReady || Boolean(pending)} onClick={checkoutCash}><DollarSign /> Complete cash sale</button>
                  {cashTendered && <p className="empty-inline wide">Displayed change: {formatCents(Math.max(0, cashTenderedAmount - sale.grandTotalAmount), sale.currency)}</p>}
                </div>
              )}
              {tender === "square" && (
                <div className="form-grid">
                  <p className="empty-inline wide">Square Terminal is initiated only through the local server route. Completion must be reported by the server before this cart resets.</p>
                  <button className="primary wide" disabled={!readyToMutate || Boolean(pending)} onClick={checkoutSquare}><CreditCard /> Send to Square Terminal</button>
                  {pendingSquare && <button className="wide" disabled={Boolean(pending)} onClick={refreshSaleStatus}><RefreshCw /> Refresh payment status</button>}
                  {pendingSquare?.kind === "square_app" && <button className="wide" disabled={Boolean(pending)} onClick={cancelPendingSquareApp}>Cancel pending Square app payment</button>}
                </div>
              )}
              {tender === "square_app" && (
                <div className="form-grid">
                  <p className="empty-inline wide">Opens the installed Square Point of Sale app on this mobile device with the current total.</p>
                  <button className="primary wide" disabled={!readyToMutate || Boolean(pending)} onClick={checkoutSquareApp}><CreditCard /> Take payment in Square app</button>
                </div>
              )}
              {tender === "store_credit" && features.storeCreditRedemptionEnabled && (
                <div className="form-grid">
                  <p className="empty-inline wide">
                    {storeCreditStatusMessage}
                  </p>
                  {creditHold?.status === "active" && <button className="wide" disabled={Boolean(pending)} onClick={releaseCreditHold}>Release store-credit hold</button>}
                  <button className="primary wide" disabled={!storeCreditReady || Boolean(pending)} onClick={checkoutStoreCredit}>Hold and capture store credit</button>
                </div>
              )}
              <button disabled={Boolean(pending) || Boolean(pendingSquare)} onClick={cancelSale}>Cancel sale</button>
            </div>
          )}

          {sale && terminalSaleStatus(sale) && (
            <div className="completion-panel" role="status" aria-live="polite">
              <CheckCircle2 />
              <div>
                <strong>{sale.saleNumber}</strong>
                <span>{sale.status} / {sale.paymentStatus}</span>
                <span>Total {formatCents(sale.grandTotalAmount, sale.currency)}</span>
              </div>
              {pendingSquare || sale.status === "payment_pending"
                ? pendingSquare?.kind === "square_app"
                  ? <button disabled={Boolean(pending)} onClick={cancelPendingSquareApp}>Cancel pending Square app payment</button>
                  : <button disabled={Boolean(pending)} onClick={refreshSaleStatus}><RefreshCw /> Refresh payment status</button>
                : <button onClick={startSale}>Start next sale</button>}
            </div>
          )}
        </aside>
      </div>

      <footer className="register-context-footer" aria-label="Active register context">
        <span><strong>Store:</strong> {storeProfile.storeDisplayName}</span>
        <span><strong>Location:</strong> {activeLocation ? `${activeLocation.name} (${activeLocation.code})` : "No authorized location"}</span>
        <span><strong>Register:</strong> {registers.find((entry) => entry.id === registerId)?.name ?? "No register"}</span>
        <span><strong>Session:</strong> {session ? `${session.sessionNumber} ${session.status}` : "Not open"}</span>
        <span><strong>Cashier:</strong> {`${currentUser.displayName} (${currentUser.role})`}</span>
      </footer>

      {customerDialogOpen && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal customer-dialog" role="dialog" aria-modal="true" aria-labelledby="customer-dialog-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Customer</p>
                <h2 id="customer-dialog-title">Find customer</h2>
              </div>
              <button aria-label="Close customer lookup" onClick={() => setCustomerDialogOpen(false)}><X /></button>
            </div>
            <div className="customer-search-row">
              <label>Customer search<input value={customerQuery} onChange={(event) => setCustomerQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void searchCustomers(); }} placeholder="Name, email, or phone" autoFocus /></label>
              <button disabled={Boolean(pending) || !customerQuery.trim()} onClick={searchCustomers}><Search /> Search customers</button>
            </div>
            <button className="text-link" disabled={Boolean(pending) || !customerQuery.trim()} onClick={createCustomer}><User /> Add customer</button>
            {customerResults.length === 0 ? <p className="empty compact-empty">Search for a customer or add one from the entered name.</p> : (
              <div className="customer-results">
                {customerResults.map((customer) => (
                  <button key={customer.id} onClick={() => { void selectCustomer(customer); }}>
                    <strong>{customer.displayName}</strong>
                    <span>{customer.emailAddress ?? customer.phoneNumber ?? customer.id}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {saleLookupOpen && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal sale-lookup-dialog" role="dialog" aria-modal="true" aria-labelledby="sale-lookup-dialog-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Sales</p>
                <h2 id="sale-lookup-dialog-title">Find completed sale</h2>
              </div>
              <button aria-label="Close sale lookup" onClick={() => setSaleLookupOpen(false)}><X /></button>
            </div>
            <SaleLookup
              query={saleLookupQuery}
              setQuery={setSaleLookupQuery}
              sales={recentSales}
              detail={selectedSaleDetail}
              giftCards={selectedSaleGiftCards}
              pending={pending === "sale-lookup" || pending === "sale-detail"}
              onSearch={loadRecentSales}
              onOpen={readSaleDetail}
              onRetryGiftCards={retryGiftCardActivation}
            />
          </section>
        </div>
      )}

      {manualItemOpen && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal manual-item-dialog" role="dialog" aria-modal="true" aria-labelledby="manual-item-dialog-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow">No inventory match</p>
                <h2 id="manual-item-dialog-title">Add manual item</h2>
              </div>
              <button aria-label="Close manual item" onClick={() => setManualItemOpen(false)}><X /></button>
            </div>
            {manualItem.scannedCode && <p className="empty-inline">Scanned: {manualItem.scannedCode}</p>}
            <div className="form-grid compact">
              <label className="wide">Description
                <input value={manualItem.description} onChange={(event) => setManualItem({ ...manualItem, description: event.target.value })} autoFocus />
              </label>
              <label>Price
                <input inputMode="decimal" value={manualItem.price} onChange={(event) => setManualItem({ ...manualItem, price: event.target.value })} placeholder="0.00" />
              </label>
              <label>Quantity
                <input inputMode="numeric" value={manualItem.quantity} onChange={(event) => setManualItem({ ...manualItem, quantity: event.target.value })} />
              </label>
            </div>
            <div className="actions">
              <button className="primary" disabled={Boolean(pending) || !manualItem.description.trim() || centsFromInput(manualItem.price) <= 0} onClick={addManualItem}>Add to sale</button>
              <button disabled={Boolean(pending)} onClick={() => setManualItemOpen(false)}>Cancel</button>
            </div>
          </section>
        </div>
      )}

      {giftCardOpen && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal manual-item-dialog" role="dialog" aria-modal="true" aria-labelledby="gift-card-dialog-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Square gift card</p>
                <h2 id="gift-card-dialog-title">Sell gift card</h2>
              </div>
              <button aria-label="Close gift card" onClick={() => setGiftCardOpen(false)}><X /></button>
            </div>
            <div className="form-grid compact">
              <label className="wide">Amount
                <input inputMode="decimal" value={giftCardAmount} onChange={(event) => setGiftCardAmount(event.target.value)} placeholder="25.00" autoFocus />
              </label>
            </div>
            <div className="actions">
              <button className="primary" disabled={Boolean(pending) || centsFromInput(giftCardAmount) <= 0} onClick={addGiftCardPurchase}>Add gift card</button>
              <button disabled={Boolean(pending)} onClick={() => setGiftCardOpen(false)}>Cancel</button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function PhoneScannerPairingCard({ session, message }: { session: ScannerSession; message?: string }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?${new URLSearchParams({ size: "220x220", data: session.url })}`;
  return (
    <div className="phone-pairing-card">
      <img src={qrUrl} alt="Phone scanner pairing QR code" />
      <div>
        <strong>Phone scanner paired</strong>
        <span>Open the camera on a phone, scan this QR code, and keep scanning item barcodes.</span>
        <small>Expires {new Date(session.expiresAt).toLocaleTimeString()}</small>
        {message && <p>{message}</p>}
      </div>
    </div>
  );
}

function RegisterCameraScanner({ onDetected, onClose }: { onDetected: (value: string) => Promise<unknown> | unknown; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const onDetectedRef = useRef(onDetected);
  const processingRef = useRef(false);
  const lastScanRef = useRef<{ value: string; scannedAt: number } | null>(null);
  const [message, setMessage] = useState("Starting camera...");

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    let active = true;
    const stop = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      controlsRef.current?.stop();
      controlsRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
    const completeScan = async (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return false;
      const normalizedIsbn = normalizeIsbn(trimmed);
      const value = isValidIsbn(normalizedIsbn) ? normalizedIsbn : trimmed;
      const lastScan = lastScanRef.current;
      const now = Date.now();
      if (processingRef.current || (lastScan?.value === value && now - lastScan.scannedAt < 1400)) return false;
      processingRef.current = true;
      lastScanRef.current = { value, scannedAt: now };
      setMessage(`Scanned ${value}. Adding to sale...`);
      try {
        await onDetectedRef.current(value);
        if (active) setMessage("Added scan. Hold up the next barcode.");
      } catch (error) {
        if (active) setMessage(error instanceof Error ? error.message : "Could not add scanned barcode.");
      } finally {
        processingRef.current = false;
      }
      return true;
    };

    const startZxingFallback = async () => {
      const video = videoRef.current;
      if (!video) return;
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        setMessage("Point the camera at the book barcode.");
        controlsRef.current = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } }, audio: false },
          video,
          (result) => {
            if (!active || !result) return;
            void completeScan(result.getText());
          },
        );
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Could not start camera barcode scanning.");
      }
    };

    const start = async () => {
      const Detector = (globalThis as typeof globalThis & { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
      if (!navigator.mediaDevices?.getUserMedia) {
        setMessage("Camera access is not available in this browser.");
        return;
      }
      if (!Detector) {
        await startZxingFallback();
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        const detector = new Detector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"] });
        setMessage("Point the camera at the book barcode.");

        const detect = async () => {
          if (!active || !videoRef.current) return;
          try {
            const [barcode] = await detector.detect(videoRef.current);
            if (barcode?.rawValue) await completeScan(barcode.rawValue);
          } catch {
            setMessage("Looking for a readable barcode...");
          }
          frameRef.current = requestAnimationFrame(() => { void detect(); });
        };
        void detect();
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Could not start camera.");
      }
    };

    void start();
    return () => {
      active = false;
      stop();
    };
  }, []);

  return (
    <div className="scanner-panel pos-camera-scanner" role="region" aria-label="Register camera barcode scanner">
      <div className="scanner-header">
        <strong>Camera scanner</strong>
        <button className="icon" aria-label="Close camera scanner" onClick={onClose}><X /></button>
      </div>
      <div className="scanner-video-wrap">
        <video ref={videoRef} className="scanner-video" muted playsInline />
        <div className="scanner-target" aria-hidden />
      </div>
      <p>{message}</p>
    </div>
  );
}

function SaleStatus({ sale }: { sale: PosSale }) {
  return (
    <dl className="metadata-fields">
      <dt>Sale</dt><dd>{sale.saleNumber}</dd>
      <dt>Status</dt><dd>{sale.status}</dd>
      <dt>Payment</dt><dd>{sale.paymentStatus}</dd>
      <dt>Inventory</dt><dd>{sale.inventoryStatus}</dd>
    </dl>
  );
}

function SaleLookup({ query, setQuery, sales, detail, giftCards = [], pending, onSearch, onOpen, onRetryGiftCards }: {
  query: string;
  setQuery: (value: string) => void;
  sales: PosSale[];
  detail: PosSale | null;
  giftCards: GiftCardIssuance[];
  pending: boolean;
  onSearch: () => void;
  onOpen: (saleId: string) => void;
  onRetryGiftCards: (saleId: string) => void;
}) {
  const rows = useMemo(() => sales, [sales]);
  return (
    <section className="sale-lookup">
      <div className="page-header compact" id="register-lookup">
        <div>
          <p className="eyebrow">Sales</p>
          <h2>Find completed sale</h2>
        </div>
      </div>
      <div className="customer-search-row">
        <label>Sale lookup<input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") onSearch(); }} placeholder="Sale number, ID, customer, or status" /></label>
        <button disabled={pending} onClick={onSearch}><Search /> Search sales</button>
      </div>
      {rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Sale</th><th>Status</th><th>Payment</th><th>Total</th><th /></tr></thead>
            <tbody>
              {rows.map((sale) => (
                <tr key={sale.id}>
                  <td>{sale.saleNumber}<small>{sale.id}</small></td>
                  <td>{sale.status}</td>
                  <td>{sale.paymentStatus}</td>
                  <td>{formatCents(sale.grandTotalAmount, sale.currency)}</td>
                  <td><button onClick={() => onOpen(sale.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && (
        <article className="register-checkout-panel">
          <h3>{detail.saleNumber}</h3>
          <SaleStatus sale={detail} />
          <ul>
            {detail.lines.map((line) => <li key={line.id}>{line.quantity} x {line.title} - {formatCents(line.lineTotalAmount, detail.currency)}</li>)}
          </ul>
          {giftCards.length > 0 && (
            <div className="gift-card-status-list">
              <h4>Gift cards</h4>
              <ul>
                {giftCards.map((giftCard) => (
                  <li key={giftCard.id}>
                    {formatCents(giftCard.amount, giftCard.currency)} - {giftCard.status}
                    {giftCard.squareGiftCardGan ? ` - ${giftCard.squareGiftCardGan}` : ""}
                    {giftCard.failureMessage ? ` - ${giftCard.failureMessage}` : ""}
                  </li>
                ))}
              </ul>
              {giftCards.some((giftCard) => giftCard.status !== "activated") && <button disabled={pending} onClick={() => onRetryGiftCards(detail.id)}><RefreshCw /> Retry activation</button>}
            </div>
          )}
          <p className="empty-inline">Read-only lookup. Return execution is not part of this interface milestone.</p>
        </article>
      )}
    </section>
  );
}

function giftCardMessage(giftCards?: GiftCardIssuance[]): string {
  if (!giftCards?.length) return "";
  const activated = giftCards.filter((giftCard) => giftCard.status === "activated");
  const failed = giftCards.filter((giftCard) => giftCard.status === "failed");
  if (failed.length) return ` ${failed.length} gift card activation${failed.length === 1 ? "" : "s"} need retry.`;
  if (activated.length) return ` ${activated.length} gift card${activated.length === 1 ? "" : "s"} activated.`;
  return " Gift card activation is pending.";
}

function ApiErrorCallout({ error }: { error: unknown }) {
  if (!error) return null;
  const code = error instanceof StaffApiError ? error.code : "unexpected";
  return <div className="error" role="alert"><AlertTriangle /> <span>{staffErrorMessage(error)} <small>({code})</small></span></div>;
}

function FeatureDisabledPanel() {
  return <div className="feature-panel"><AlertTriangle /> Store-credit tender is not enabled here. Cash and Square Terminal remain available when the sale is otherwise eligible.</div>;
}

function supportsRegisterCameraControls(): boolean {
  const userAgent = navigator.userAgent;
  const isMobileUserAgent = /Android|iPhone|iPad|iPod/i.test(userAgent);
  const isTouchTablet = navigator.maxTouchPoints > 1 && (window.matchMedia?.("(pointer: coarse)").matches ?? false);
  return isMobileUserAgent || isTouchTablet;
}

function isPhoneLikeDevice(): boolean {
  const userAgent = navigator.userAgent;
  const isPhoneUserAgent = /Android.*Mobile|iPhone|iPod/i.test(userAgent);
  const isSmallCoarsePointer = window.matchMedia?.("(max-width: 767px) and (pointer: coarse)").matches ?? false;
  return isPhoneUserAgent || isSmallCoarsePointer;
}

function parseSquarePosCallback(params: URLSearchParams): {
  status: "completed" | "failed" | "cancelled" | "unknown" | "requires_reconciliation";
  providerPaymentId?: string;
  failureCode?: string;
  failureMessage?: string;
} {
  const iosData = params.get("data");
  if (iosData) {
    try {
      const parsed = JSON.parse(iosData) as Record<string, unknown>;
      const errorCode = typeof parsed.error_code === "string" ? parsed.error_code : undefined;
      const transactionId = typeof parsed.transaction_id === "string" ? parsed.transaction_id : undefined;
      if (errorCode) return squareCallbackError(errorCode);
      if (transactionId) return { status: "completed", providerPaymentId: transactionId };
    } catch {
      return { status: "requires_reconciliation", failureCode: "invalid_callback_data", failureMessage: "Square app returned unreadable callback data" };
    }
  }
  const androidError = params.get("com.squareup.pos.ERROR_CODE") ?? params.get("error_code") ?? undefined;
  const androidTransactionId = params.get("com.squareup.pos.SERVER_TRANSACTION_ID") ?? params.get("transaction_id") ?? undefined;
  if (androidError) return squareCallbackError(androidError);
  if (androidTransactionId) return { status: "completed", providerPaymentId: androidTransactionId };
  return { status: "cancelled", failureCode: "missing_transaction_id", failureMessage: "Square app returned without a payment transaction" };
}

function isSquarePosCallback(params: URLSearchParams): boolean {
  if (params.get("square_pos_callback") === "1") return true;
  return params.has("data")
    || params.has("transaction_id")
    || params.has("error_code")
    || params.has("com.squareup.pos.SERVER_TRANSACTION_ID")
    || params.has("com.squareup.pos.ERROR_CODE");
}

function squareCallbackError(code: string): {
  status: "failed" | "cancelled";
  failureCode: string;
  failureMessage: string;
} {
  const normalized = code.toLowerCase();
  return {
    status: normalized.includes("cancel") ? "cancelled" : "failed",
    failureCode: code,
    failureMessage: `Square app returned ${code}`,
  };
}

function terminalSaleStatus(sale: PosSale): boolean {
  return ["completed", "cancelled", "payment_failed", "payment_pending", "completion_required"].includes(sale.status) || sale.paymentStatus === "requires_reconciliation";
}

function saleCanStartNext(sale: PosSale): boolean {
  return ["completed", "cancelled", "payment_failed"].includes(sale.status);
}

function centsFromInput(value: string): number {
  const trimmed = value.trim().replace(/[$,]/g, "");
  if (!trimmed) return 0;
  const [whole, fraction = ""] = trimmed.split(".");
  const sign = whole.startsWith("-") ? -1 : 1;
  const absoluteWhole = whole.replace("-", "");
  return sign * (Number(absoluteWhole || 0) * 100 + Number(fraction.padEnd(2, "0").slice(0, 2) || 0));
}

function centsToInput(amount: number): string {
  return (Math.max(0, Math.floor(amount || 0)) / 100).toFixed(2);
}

function formatCents(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount / 100);
}

function formatDiscountValue(discount: PosDiscountDefinition, currency: string): string {
  return discount.discountType === "percentage" ? `${(discount.valueAmount / 100).toFixed(2).replace(/\.00$/, "")}%` : formatCents(discount.valueAmount, currency);
}

function formatCreditBalance(amount: number, currency: string, creditMode: CreditAccount["creditMode"]): string {
  if (creditMode === "one_for_one") return `${amount} credit${amount === 1 ? "" : "s"}`;
  return formatCents(amount, currency);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(value));
}

function loyaltyQualificationText(sale: PosSale, balance: LoyaltyBalance, selectedRewardId: string): string {
  if (sale.status !== "draft") return "Rewards can only be applied while the sale is editable.";
  if (!sale.lines.length) return "Add qualifying items before applying a reward.";
  if (!selectedRewardId) return "Choose an available reward to apply it before tender.";
  const reward = balance.availableRewards.find((candidate) => candidate.id === selectedRewardId);
  if (!reward) return "Selected reward is no longer configured.";
  if (balance.balance < reward.pointsCost) return `${reward.pointsCost - balance.balance} more point(s) needed for this reward.`;
  if (sale.amountDue <= 0) return "This sale is already fully covered.";
  return `${reward.name} can reduce this sale by up to ${formatCents(reward.rewardAmount, reward.currency)}.`;
}

function discountRuleIsEligibleForSale(discount: PosDiscountDefinition, sale: PosSale): boolean {
  if (!sale.lines.length) return false;
  if (!discountDateWindowIsOpen(discount)) return false;
  const subtotal = sale.lines.reduce((sum, line) => sum + line.originalUnitPriceAmount * line.quantity, 0);
  if (discount.minimumSubtotalAmount > 0 && subtotal < discount.minimumSubtotalAmount) return false;
  const itemCount = sale.lines.reduce((sum, line) => sum + line.quantity, 0);
  if (discount.minimumItemCount > 0 && itemCount < discount.minimumItemCount) return false;
  const matchingLines = sale.lines.filter((line) => discountRuleMatchesLine(discount, line));
  if (!matchingLines.length) return false;
  if (discount.minimumMatchingItemCount > 0 && matchingLines.reduce((sum, line) => sum + line.quantity, 0) < discount.minimumMatchingItemCount) return false;
  return true;
}

function discountDateWindowIsOpen(discount: PosDiscountDefinition): boolean {
  const now = new Date();
  if (discount.startsAt && new Date(discount.startsAt) > now) return false;
  if (discount.endsAt && new Date(discount.endsAt) < now) return false;
  if (discount.daysOfWeek.length) {
    const day = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][now.getDay()];
    if (!discount.daysOfWeek.includes(day)) return false;
  }
  const time = now.toTimeString().slice(0, 5);
  if (discount.startTime && time < discount.startTime) return false;
  if (discount.endTime && time > discount.endTime) return false;
  return true;
}

function discountRuleMatchesLine(discount: PosDiscountDefinition, line: PosSale["lines"][number]): boolean {
  return discountTextMatches(discount.productType, line.productType)
    && discountTextMatches(discount.formatMatch, line.format)
    && discountTextMatches(discount.conditionMatch, line.condition)
    && discountTextMatches(discount.skuMatch, line.sku || line.storeBarcode)
    && discountTextMatches(discount.titleMatch, line.title);
}

function discountTextMatches(needle: string | undefined, haystack: string | undefined): boolean {
  if (!needle) return true;
  return String(haystack ?? "").toLowerCase().includes(needle.toLowerCase());
}

function retainedKey(kind: string, saleId: string, fingerprint: string): string {
  const storageKey = `spine_pos_${kind}_${saleId}`;
  const existing = localStorage.getItem(storageKey);
  if (existing) {
    try {
      const parsed = JSON.parse(existing) as { fingerprint?: string; key?: string };
      if (parsed.fingerprint === fingerprint && parsed.key) return parsed.key;
    } catch {
      localStorage.removeItem(storageKey);
    }
  }
  const key = crypto.randomUUID();
  localStorage.setItem(storageKey, JSON.stringify({ fingerprint, key }));
  return key;
}

function clearSaleKeys(saleId: string) {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith("spine_pos_") && key.endsWith(`_${saleId}`)) localStorage.removeItem(key);
  }
  clearPendingSquare();
}

function readPendingSquare(): PendingSquare | null {
  const raw = localStorage.getItem(pendingSquareStorageKey);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingSquare;
  } catch {
    localStorage.removeItem(pendingSquareStorageKey);
    return null;
  }
}

function writePendingSquare(value: PendingSquare) {
  localStorage.setItem(pendingSquareStorageKey, JSON.stringify(value));
}

function clearPendingSquare() {
  localStorage.removeItem(pendingSquareStorageKey);
}
